import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { DefaultValues, UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { hasValue } from 'utils/hasValue';

import type {
  AuthScheme,
  IntegrationBusinessUnit,
  IntegrationValidationError,
  WebhookCreateRequestEventsItemOneOf,
  WebhookCreateResponse,
  WebhookCreateResponseConflictsItem,
  WebhookEvent
} from 'api';
import {
  getGetIntegrationWebhooksQueryKey,
  usePostIntegrationWebhooks
} from 'api';
import type { AddWebhookFormValues } from 'validators/addWebhookModal';
import { addWebhookModalSchema } from 'validators/addWebhookModal';

import { useOrganisationId } from 'hooks/context';

import {
  ADD_WEBHOOK_GENERIC_ERROR,
  addWebhookSuccessMessage,
  alreadyActiveMessage
} from './constants';

import { useAuthenticationKeys } from '../../hooks/useAuthenticationKeys';
import { useBusinessUnits } from '../../hooks/useBusinessUnits';
import { useWebhookEvents } from '../../hooks/useWebhookEvents';
import { extractIntegrationValidationError } from '../../utils/extractIntegrationValidationError';

type UseAddWebhookFormArgs = {
  onClose: () => void;
  onConflicts: (conflicts: WebhookCreateResponseConflictsItem[]) => void;
};

export type UseAddWebhookForm = {
  form: UseFormReturn<AddWebhookFormValues>;
  businessUnits: IntegrationBusinessUnit[];
  authKeys: AuthScheme[];
  events: WebhookEvent[];
  eventsLoading: boolean;
  eventsError: boolean;
  submit: ReturnType<UseFormReturn<AddWebhookFormValues>['handleSubmit']>;
  cancel: () => void;
  isSubmitting: boolean;
};

export const useAddWebhookForm = ({
  onClose,
  onConflicts
}: UseAddWebhookFormArgs): UseAddWebhookForm => {
  const organisationId = useOrganisationId<number>();
  const queryClient = useQueryClient();
  const openNotification = useOpenNotification();
  const createMutation = usePostIntegrationWebhooks();

  const { keys: authKeys } = useAuthenticationKeys();
  const { businessUnits, defaultUuid } = useBusinessUnits();
  const {
    events,
    isLoading: eventsLoading,
    isError: eventsError
  } = useWebhookEvents();

  const defaultFormats = useMemo(
    () =>
      Object.fromEntries(
        events.map((event) => [event.event, event.default_format])
      ),
    [events]
  );

  const initialValues: DefaultValues<AddWebhookFormValues> = {
    endpointUrl: '',
    businessUnitUuid: defaultUuid,
    xAuthUuid: undefined,
    selectedEvents: [],
    eventFormats: defaultFormats
  };

  const form = useForm<AddWebhookFormValues>({
    mode: 'onChange',
    resolver: yupResolver(addWebhookModalSchema),
    values: initialValues as AddWebhookFormValues,
    resetOptions: { keepDirtyValues: true }
  });

  const cancel = () => {
    onClose();
  };

  const handleSuccess = (response: WebhookCreateResponse) => {
    queryClient.invalidateQueries({
      queryKey: getGetIntegrationWebhooksQueryKey(organisationId)
    });

    if (response.data.length > 0) {
      openNotification({
        kind: 'success',
        message: addWebhookSuccessMessage(response.data.length)
      });
    }

    const alreadyActiveEvents = response.conflicts
      .filter((conflict) => conflict.reason === 'already_active')
      .map((conflict) => conflict.event)
      .filter(hasValue);

    if (alreadyActiveEvents.length > 0) {
      openNotification({
        kind: 'info',
        message: alreadyActiveMessage(alreadyActiveEvents)
      });
    }

    cancel();

    const reactivatable = response.conflicts.filter(
      (conflict) =>
        conflict.reason === 'inactive_exists' && hasValue(conflict.uuid)
    );

    if (reactivatable.length > 0) {
      onConflicts(reactivatable);
    }
  };

  const handleError = (error: IntegrationValidationError | null) => {
    openNotification({
      kind: 'error',
      message: extractIntegrationValidationError(
        error,
        ADD_WEBHOOK_GENERIC_ERROR
      )
    });
  };

  const submit = form.handleSubmit((values) => {
    const events: WebhookCreateRequestEventsItemOneOf[] =
      values.selectedEvents.map((event) => ({
        event,
        format: values.eventFormats[event]
      }));

    createMutation.mutate(
      {
        organisationId,
        data: {
          destination_url: values.endpointUrl.trim(),
          business_unit_uuid: values.businessUnitUuid,
          auth_uuid: values.xAuthUuid,
          events
        }
      },
      { onSuccess: handleSuccess, onError: handleError }
    );
  });

  return {
    form,
    businessUnits,
    authKeys,
    events,
    eventsLoading,
    eventsError,
    submit,
    cancel,
    isSubmitting: createMutation.isPending
  };
};
